import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BusinessProfile } from '../../../../../../common/src/lib/models/business-profile.model';
import { UserProfile } from '../../../../../../common/src/lib/models/user-profile.model';
import { BusinessAddressComponents } from '../../../../../../common/src/lib/services/places.service';
import { UsersService } from '../../../services/users.service';
import { BusinessService } from '../../../services/business.service';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ProfileFieldComponent } from '../../../components/profile-field/profile-field.component';
import { capitalizeFirstChar, deepDeleteUndefined } from 'projects/common/src/public-api';
import { AddressComponents, addressComponentsEmpty } from 'projects/common/src/lib/models/address-comments.model';
import { ConfirmationDialog } from '../../../../../../common/src/lib/modals/confirmation-dialog/confirmation-dialog.component';
import { ModalsService } from 'projects/common/src/lib/services/modals.service';
import { componentsToString } from 'projects/common/src/lib/pipes/address.pipe';
import { OnboardingService } from "../../../services/onboarding.service";
import { Router } from "@angular/router";
import { BannerComponent } from "../../../components/onboarding-banner/banner.component";
import { QuickbooksService } from "../../../services/quickbooks.service";
import { showSnackbar } from "../../../../../../common/src/lib/components/snackbar/snackbar.component";

type NuveiData = {
  dataType: 'banner',
  type: BannerComponent['type'],
  message: string,
  action?: {
    text: string,
    class: string,
    fn: () => void
  }
} | {
  dataType: 'nuvei_data',
  bankAccountNumber: string
}

@Component({
  selector: 'app-business',
  templateUrl: './business.component.html',
  styleUrls: ['./business.component.scss']
})
export class BusinessComponent {

  @ViewChild('businessNameInputField') businessNameInputField!: ProfileFieldComponent<string>;
  
  @Input() profile!: BusinessProfile;
  @Input() user!: UserProfile;

  @Output() changeTab = new EventEmitter<string>();

  phoneNumberSubject = new BehaviorSubject('');

  disableFieldEdit$ = this.usersService.currentUser$.pipe(
    map(user => user?.role !== 'owner' && user?.role !== 'admin'),
  );

  phoneNumberError?: string;

  status$ = this.onboardingService.onboardingData$.pipe(map(data => data?.applicationStatus));
  missingDataList$ = this.onboardingService.onboardingData$.pipe(map(data => data?.missingDataList ?? []));
  showNuveiData$ = this.onboardingService.onboardingData$.pipe(
      map(data => {
        if(!data)
          return null;
        console.log(data.applicationStatus)
        if(data.applicationStatus === null) {
          return {
            dataType: 'banner',
            type: 'success',
            message: 'Start processing payments with Nuvei',
            action: {
              text: 'Start >',
              class: 'green-text',
              fn: this.navigateToApplication.bind(this)
            },
            bankAccountNumber: data.bankAccountNumber
          } as NuveiData;
        }
        return {
          'created': {
            dataType: 'banner',
            type: 'success',
            message: 'Complete Nuvei’s application',
            action: {
              text: 'Continue >',
              class: 'green-text',
              fn: this.navigateToApplication.bind(this)
            },
            bankAccountNumber: data.bankAccountNumber
          },
          'pending': {
            dataType: 'banner',
            type: 'warning',
            message: 'Your payment processing application is in review!',
            bankAccountNumber: data.bankAccountNumber
          },
          'sent_missing_data_to_nuvei': {
            dataType: 'banner',
            type: 'warning',
            message: 'The additional information has be sent to Nuvei and is in review!',
            bankAccountNumber: data.bankAccountNumber
          },
          'rejected': {
            dataType: 'banner',
            type: 'error',
            message: 'Your application for payment processing was rejected',
            bankAccountNumber: data.bankAccountNumber
          },
          'missing_data': {
            dataType: 'banner',
            message: 'More information needed. Please check the support chat in the Help tab',
            type: 'error',
            bankAccountNumber: data.bankAccountNumber
          },
          'approved': {
            dataType: 'nuvei_data',
            bankAccountNumber: data.bankAccountNumber
          },
        }[data.applicationStatus] as NuveiData;
      }),
  );

  qbAuthLoading = false;
  qbTestApiLoading = false;

  constructor(
    private router: Router,
    private usersService: UsersService,
    private businessService: BusinessService,
    private modalsService: ModalsService,
    private onboardingService: OnboardingService,
    public quickbooksService: QuickbooksService,
  ) { }

  capitalizeFirstChar = capitalizeFirstChar;

  navigateToSupport() {
    this.changeTab.emit('Help');
  }

  navigateToApplication() {
    this.router.navigate(['/payment-application']);
  }

  async onBusinessPhoneNumberEditClick() {
    const editDisabled = await this.disableFieldEdit$.pipe(take(1)).toPromise();
    if(editDisabled)
      this.phoneNumberError = 'Business phone number can only be edit by an admin';
  }

  businessNameToString(value?: string | BusinessAddressComponents) {
    if(!value) return '';
    return typeof value === 'string' ? value : value.name;
  }

  addressToString(value?: AddressComponents) {
    if(!value) return '';
    return componentsToString(value);
  }

  async saveBusinessName(value: string | BusinessAddressComponents) {
    const businessName = (typeof value === 'string' ? value : value.name).trim();
    if(businessName === this.profile.businessName) {
      return null;
    }
    if(businessName.length === 0) {
      return 'Business name is required';
    }

    if(typeof value === 'object' && !addressComponentsEmpty(value))
      this.modalsService.open(ConfirmationDialog, {
        data: {
          title: `Set address`,
          message: `Would you also like to set the address ${componentsToString(value)} for your business?`,
          actionTitle: 'Yes',
          cancelTitle: 'No',
          action: async () => {
            this.saveAddress(value);
          },
          actionColor: 'primary',
        }
      });

    const data: any = { businessName: businessName };
    await this.businessService.updateBusiness(data);
    return null;
  }

  async saveAddress(value?: AddressComponents) {
    if(!value) {
      return 'Address is required';
    }
    if(typeof value === 'string') {
      return 'Select a full address option from the dropdown';
    }
    if(addressComponentsEmpty(value)) {
      return 'Select a full address option from the dropdown';
    }
    
    await this.businessService.updateBusiness({
      address: deepDeleteUndefined(value)
    });
    return null;
  }

  async saveBusinessPhoneNumber(value: string) {
    if(value === this.profile.phoneNumber) {
      return null;
    }

    if(value === '') {
      return 'Business phone number is required';
    } else if (value.length !== 0 && value.length !== 10) {
      return 'Invalid business phone number. Re-enter';
    }

    const data: any = { phoneNumber: value };
    await this.businessService.updateBusiness(data);
    return null;
  }

  async connectQb() {
    try {
      this.qbAuthLoading = true;
      window.location.href = await this.quickbooksService.getAuthUrl();
    } catch (e) {
      console.log(e);
    } finally {
      this.qbAuthLoading = false;
    }
  }

  async disconnectQb() {
    await this.modalsService.open(ConfirmationDialog, {
      data: {
        title: 'Disconnect Quickbooks',
        message: 'Are you sure you want to disconnect Quickbooks from your business?',
        actionTitle: 'Yes',
        actionColor: 'warn',
        cancelTitle: 'No',
        action: async () =>  {
          try {
            await this.quickbooksService.disconnect();
          } catch (e) {
            console.log(e);
          }
        }
      }
    });
  }

  async testQBApi() {
    try {
      this.qbTestApiLoading = true;
      const res = await this.quickbooksService.testQBApi();
      console.log(res);
      return res;
    } catch (e) {
      console.log(e);
      return null;
    } finally {
      this.qbTestApiLoading = false;
    }
  }
}
